/**
 * Anpassbares Panel-Grid. Echtes 12-Spalten-CSS-Grid: Kacheln per `grid-column:
 * span <span>`, `align-items: stretch` → Panels einer Reihe sind gleich hoch
 * (bündige Unterkanten), Höhe automatisch nach Reihenhöhe, Inhalt oben.
 * Im Edit-Modus: Drag-Reorder (window-getriebene Pointer Events), Größen-Presets
 * (¼/⅓/½/▣), Ein-/Ausblenden + Hidden-Tray + Reset, ◀▶-Fallback. Persistenz pro
 * View über layout_*-Actions (debounced). Ghost folgt dem Cursor (Portal an body).
 */

import { useSignal } from "@preact/signals";
import type { Signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { createPortal } from "preact/compat";
import type { ComponentChildren } from "preact";
import { api } from "../store";
import type { ApiResponse } from "../api";
import {
  mergeLayout, reorder, setSpan, setVisible, resetToDefault, visibleSorted,
  SPANS, type LayoutEntry, type PanelDef,
} from "./layout";

export interface PanelItem extends PanelDef {
  title: string;
  render: () => ComponentChildren;
}

const SPAN_LABEL: Record<number, string> = { 3: "¼", 4: "⅓", 6: "½", 12: "▣" };

interface DragState { id: string; title: string; x: number; y: number; offX: number; offY: number; w: number }

export function PanelGrid({ view, items, edit }: { view: string; items: PanelItem[]; edit: Signal<boolean> }) {
  const layout = useSignal<LayoutEntry[]>(mergeLayout(null, items));
  const drag = useSignal<DragState | null>(null);
  const dropId = useSignal<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemsKey = items.map((i) => i.id).join("|");
  const byId = new Map(items.map((i) => [i.id, i]));
  // Das ROH gespeicherte Layout (oder null) getrennt halten — NICHT direkt in
  // `layout` einkochen. Plus die aktuellen Items als Ref. Hintergrund-Bugfix:
  // `layout_get` ist async; früher wurde sein Ergebnis mit den Items aus der
  // MOUNT-Closure gemerged. Lädt eine View ihre Items erst async (z.B. Commands:
  // cmd_defs nach dem Mount), waren das beim Auflösen NOCH die LEEREN Items →
  // `mergeLayout(saved, [])` = [] → Grid blieb leer, obwohl Daten da waren.
  // Jetzt: Merge IMMER gegen die aktuellen Items (Ref), gespeichertes via Ref.
  const savedLayout = useRef<LayoutEntry[] | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api.call<ApiResponse & { layout: LayoutEntry[] | null }>("layout_get", { view });
        if (alive && r.ok) {
          savedLayout.current = r.layout;
          layout.value = mergeLayout(r.layout, itemsRef.current);
        }
      } catch { /* ignorieren */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    // Items haben sich geändert (z.B. async nachgeladen) → gespeichertes Layout
    // erneut gegen die AKTUELLEN Items mergen (gespeicherte order/span/visible
    // bleiben erhalten, neue Panels kommen dazu).
    layout.value = mergeLayout(savedLayout.current, items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  function persist() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void api.call("layout_save", { view, layout: layout.value }), 600);
  }
  function apply(next: LayoutEntry[]) { layout.value = next; persist(); }

  async function reset() {
    layout.value = resetToDefault(items);
    try { await api.call("layout_reset", { view }); } catch { /* egal */ }
  }

  /* ---------- Drag-Reorder: window-getrieben (zuverlässige Event-Zustellung) ---------- */
  function startDrag(e: PointerEvent, id: string, title: string) {
    if (e.button !== undefined && e.button !== 0) return; // nur linke Maustaste
    e.preventDefault();
    // Greif-Offset relativ zur gegriffenen Titelleiste merken (Ghost klebt am Cursor).
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    drag.value = { id, title, x: e.clientX, y: e.clientY, offX: e.clientX - rect.left, offY: e.clientY - rect.top, w: rect.width };

    const onMove = (ev: PointerEvent) => {
      const d = drag.value;
      if (!d) return;
      drag.value = { ...d, x: ev.clientX, y: ev.clientY };
      // Gezogene Kachel ist pointer-events:none -> elementFromPoint trifft das Drop-Ziel.
      const cell = document.elementFromPoint(ev.clientX, ev.clientY)?.closest<HTMLElement>(".pgrid-cell");
      const overId = cell?.dataset.id;
      if (!overId || overId === d.id) { dropId.value = null; return; }
      dropId.value = overId;
      const toIndex = visibleSorted(layout.value).findIndex((x) => x.id === overId);
      if (toIndex >= 0) apply(reorder(layout.value, d.id, toIndex)); // live umsortieren
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      drag.value = null;
      dropId.value = null;
      persist();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  }

  const vis = visibleSorted(layout.value);
  const hidden = layout.value.filter((e) => !e.visible);
  const nudge = (id: string, dir: -1 | 1) => apply(reorder(layout.value, id, vis.findIndex((x) => x.id === id) + dir));

  return (
    <div class="pgrid-wrap">
      {edit.value && (
        <div class="pgrid-toolbar">
          <span class="label">Layout anpassen</span>
          <div class="row" style="flex-wrap:wrap;gap:6px">
            {hidden.map((h) => (
              <button key={h.id} class="btn sm" onClick={() => apply(setVisible(layout.value, h.id, true))}>
                + {byId.get(h.id)?.title ?? h.id}
              </button>
            ))}
          </div>
          <div class="grow" />
          <button class="btn sm ghost" onClick={() => void reset()}>Zurücksetzen</button>
        </div>
      )}

      <div class={`pgrid ${edit.value ? "editing" : ""}`}>
        {vis.map((e) => {
          const item = byId.get(e.id);
          if (!item) return null;
          const dragging = drag.value?.id === e.id;
          return (
            <div
              key={e.id}
              class={`pgrid-cell ${dragging ? "dragging" : ""} ${dropId.value === e.id ? "drop" : ""}`}
              data-id={e.id}
              style={`grid-column:span ${e.span}`}
            >
              {edit.value && (
                <div class="pgrid-bar" onPointerDown={(ev) => startDrag(ev as unknown as PointerEvent, e.id, item.title)} title="Header ziehen zum Anordnen">
                  <span class="pgrid-handle">⠿ {item.title}</span>
                  {/* Buttons stoppen den Drag (onPointerDown), bleiben aber klickbar. */}
                  <div class="row" style="gap:4px" onPointerDown={(ev) => (ev as unknown as PointerEvent).stopPropagation()}>
                    <button class="seg" onClick={() => nudge(e.id, -1)} title="nach vorne" aria-label="nach vorne">◀</button>
                    <button class="seg" onClick={() => nudge(e.id, 1)} title="nach hinten" aria-label="nach hinten">▶</button>
                    {SPANS.map((s) => (
                      <button key={s} class={`seg ${e.span === s ? "on" : ""}`} onClick={() => apply(setSpan(layout.value, e.id, s))}>{SPAN_LABEL[s]}</button>
                    ))}
                    <button class="seg" onClick={() => apply(setVisible(layout.value, e.id, false))} title="ausblenden" aria-label="ausblenden">✕</button>
                  </div>
                </div>
              )}
              {item.render()}
            </div>
          );
        })}
      </div>

      {/* Ghost per Portal an document.body — raus aus jedem transformierten
          Vorfahren (sonst ist position:fixed nicht mehr viewport-relativ). */}
      {drag.value && createPortal(
        <div
          class="pgrid-ghost"
          style={`left:${drag.value.x - drag.value.offX}px;top:${drag.value.y - drag.value.offY}px;width:${drag.value.w}px`}
        >
          ⠿ {drag.value.title}
        </div>,
        document.body,
      )}
    </div>
  );
}
